import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
}                          from '@angular/core';
import {filter, includes}  from 'lodash';
import {CropTypeStore}     from '../../../../../stores/base-data/crop.types.store';
import {ObjectFactory}     from 'ts-tooling';
import {NotifyStore}       from '../../../../../stores/dialog/notify.store';
import {ComboBoxComponent} from '@progress/kendo-angular-dropdowns';
import ICropTypes = Data.BaseData.ICropTypes;
import ICropRotationCell = Data.FieldManagement.ICropRotationCell;
import IApValidationResult = Data.Api.Validation.IApValidationResult;
import { ApKendoIconService } from 'src/app/icons/ap-kendo-icon.service';

/**
 * displays the Controls to change the current MainCrop or SecondCrop
 */
@Component({
  selector: 'ap-crop-rotation-detail-big-cell',
  templateUrl: './detail-cell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailCellComponent implements OnChanges {
  protected readonly ApKendoIconService = ApKendoIconService;
  public static NoSelectedCropNotification: IApValidationResult = {
    Counter: 1,
    DisplayTimeout: 3,
    ErrorKey: 'Global__CropType_NotSelected',
    Level: Data.Api.Validation.ApValidationLevel.Error,
    Parameters: [],
    UserName: null,
    UserId: null,
  };
  @ViewChild('mainCropComboBox', {static: false}) mainCropComboBox: ComboBoxComponent;
  @ViewChild('secondCropComboBox', {static: false}) secondCropComboBox: ComboBoxComponent;
  /**
   * the Cell Data
   */
  @Input() public data: ICropRotationCell = {
    Year: null,
    YearIndex: null,
    FieldId: null,
    MainCrop: null,
    MainCropYield: 0,
    MainCropStraw: false,
    SecondCrop: null,
    SecondCropYield: 0,
  };
  /**
   * the Checkbox Id for the Straw Harvested Checkbox
   */
  @Input() public strawHarvestedId: string;
  /**
   * a List of CropTypes for the current Farm
   */
  @Input() public availableCropTypes: ICropTypes[] = [];
  /**
   * are the Controls available for edit?
   */
  @Input() public disableControls = false;
  /**
   * is the current Cell saving by the Backend?
   */
  @Input() public saving = false;
  /**
   * fires when the Value in the Cell was changed
   */
  @Output() public saveCell = new EventEmitter<ICropRotationCell>();
  /**
   * fires when the Cell gets a focus
   */
  @Output() public getFocus = new EventEmitter<[string, number]>();
  /**
   * fires when the Cell lost the Focus
   */
  @Output() public lostFocus = new EventEmitter<[string, number]>();

  /**
   * the Default CropType that was set when a new Main/Second Crop was created
   */
  public get DefaultCrop(): ICropTypes {
    return this.availableCropTypes[0];
  }

  /**
   * the MainCrop Object
   */
  public mainCrop: ICropTypes;
  /**
   * the SecondCrop Object
   */
  public secondCrop: ICropTypes;

  public mainCropSource: ICropTypes[] = [];
  public secondCropSource: ICropTypes[] = [];

  constructor(private cropTypeStore: CropTypeStore,
              private notifyStore: NotifyStore,
              private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.mainCrop = changes.data.currentValue.MainCrop ?
        this.cropTypeStore.getDefaultCroptype(changes.data.currentValue.MainCrop) :
        null;
      this.secondCrop = changes.data.currentValue.SecondCrop ?
        this.cropTypeStore.getDefaultCroptype(changes.data.currentValue.SecondCrop) :
        null;
    }

    if (changes.availableCropTypes) {
      this.mainCropSource = changes.availableCropTypes.currentValue;
      this.secondCropSource = changes.availableCropTypes.currentValue;

      if (this.mainCrop && !this.mainCropSource.Any(_ => _.Id === this.mainCrop?.Id)) {
        this.mainCropSource = [this.mainCrop].concat(this.mainCropSource);
      }
      if (this.secondCrop && !this.secondCropSource.Any(_ => _.Id === this.secondCrop?.Id)) {
        this.secondCropSource = [this.secondCrop].concat(this.secondCropSource);
      }
    }
  }

  /**
   * Starw Harvested was changed
   */
  public changeStrawHarvested(): void {
    this.data.MainCropStraw = !this.data.MainCropStraw;
    this.saveCell.emit(ObjectFactory.Copy(this.data));
  }

  /**
   * MainCrop was selected
   */
  public onMainCropChange(e: ICropTypes): void {
    if (!e) {
      this.mainCrop = null;
      this.data.MainCrop = null;
      this.data.MainCropYield = 0;
      this.data.MainCropStraw = false;
      this.lostFocus.emit([this.data.FieldId.toString(), this.data.Year]);
    } else {
      const ct = this.mainCropSource.Find(_ => _.Id === e.Id);
      if (!ct) {
        console.warn(`croptype with Shortname ${e} not found`);
        return;
      }
      this.data.MainCrop = ct.Id;
      this.data.MainCropYield = ct.ExpectedYield;
      this.data.MainCropStraw = false;
    }
    this.saveCell.emit(ObjectFactory.Copy(this.data));
  }

  /**
   * a Second Crop was selected
   */
  public onSecondCropChange(e: ICropTypes): void {
    if (!e) {
      this.secondCrop = null;
      this.data.SecondCrop = null;
      this.data.SecondCropYield = 0;
      this.lostFocus.emit([this.data.FieldId.toString(), this.data.Year]);
      this.saveCell.emit(ObjectFactory.Copy(this.data));
      return;
    }
    const ct = this.secondCropSource.Find(_ => _.Id === e.Id);
    if (!ct) {
      console.warn(`croptype with Shortname ${e} not found`);
      return;
    }
    this.data.SecondCrop = ct.Id;
    this.data.SecondCropYield = 0;
    this.secondCrop = this.cropTypeStore.getDefaultCroptype(ct.Id);
    this.saveCell.emit(ObjectFactory.Copy(this.data));
  }

  /**
   * the MainCrop Yield was changed
   */
  public onMainCropYieldChange(e: Event): void {
    this.data.MainCropYield = this.parseYield((e.target as HTMLInputElement).value);
    this.saveCell.emit(ObjectFactory.Copy(this.data));
  }

  /**
   * the SecondCrop Yield was changed
   */
  public onSecondCropYieldChange(e: Event): void {
    this.data.SecondCropYield = this.parseYield((e.target as HTMLInputElement).value);
    this.saveCell.emit(ObjectFactory.Copy(this.data));
  }

  /**
   * a new MainCrop was created
   */
  public onAddMainCrop(): void {
    if (!this.DefaultCrop) {
      this.notifyStore.addMessage(DetailCellComponent.NoSelectedCropNotification);
      return;
    }
    this.mainCrop = this.DefaultCrop;
    this.data.MainCrop = this.DefaultCrop.Id;
    this.data.MainCropYield = this.DefaultCrop.ExpectedYield;
    this.data.MainCropStraw = false;
    this.getFocus.emit([this.data.FieldId.toString(), this.data.Year]);
    // give a Copy otherwise this.data mutates to read only
    this.saveCell.emit(ObjectFactory.Copy(this.data));
    // make sure the mainCropComboBox is shown
    this.changeDetectorRef.detectChanges();
    // ...and set focus to fix APV49-1832
    this.mainCropComboBox?.focus();
  }

  /**
   * a Second Crop was created
   */
  public onAddSecondCrop(): void {
    if (!this.DefaultCrop) {
      this.notifyStore.addMessage(DetailCellComponent.NoSelectedCropNotification);
      return;
    }
    this.secondCrop = this.DefaultCrop;
    this.data.SecondCrop = this.DefaultCrop.Id;
    this.data.SecondCropYield = 0;
    this.getFocus.emit([this.data.FieldId.toString(), this.data.Year]);
    this.saveCell.emit(ObjectFactory.Copy(this.data));
    // make sure the secondCropComboBox is shown
    this.changeDetectorRef.detectChanges();
    // ...and set focus to fix APV49-1832
    this.secondCropComboBox?.focus();
  }

  public onYieldKeyUp(): void {
    setTimeout(() => {
      this.saveCell.emit(ObjectFactory.Copy(this.data));
    }, 1000);
  }

  public onGetFocus(): void {
    this.getFocus.emit([this.data.FieldId.toString(), this.data.Year]);
  }

  public onLostFocus(): void {
    this.lostFocus.emit([this.data.FieldId.toString(), this.data.Year]);
  }

  public handleMainCropFilter(valueToSearch: string): void {
    if (!valueToSearch) {
      this.mainCropSource = this.availableCropTypes;
    }
    this.mainCropSource = filter(this.availableCropTypes,
      (i: any) => includes(i?.Description?.toLowerCase(), valueToSearch.toLowerCase()));
  }

  public handleSecondCropFilter(valueToSearch: string): void {
    if (!valueToSearch) {
      this.secondCropSource = this.availableCropTypes;
    }
    this.secondCropSource = filter(this.availableCropTypes,
      (i: any) => includes(i?.Description?.toLowerCase(), valueToSearch.toLowerCase()));
  }

  public onKeyDown($event: KeyboardEvent): void {
    if (!$event || $event?.key?.toLowerCase() !== 'enter') {
      return;
    }
    this.saveCell.emit(ObjectFactory.Copy(this.data));
    this.lostFocus.emit([this.data.FieldId.toString(), this.data.Year]);
  }

  private parseYield(value: string): number {
    const parsedYield = parseFloat(value);
    return parsedYield > 0 ? parsedYield : 0;
  }
}
